// product-price-history.js
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/** <product-price-history> */
class ProductPriceHistory extends HTMLElement {
    /** @type {AbortController | null} */
    aborter = null;

    connectedCallback() {
        const section = this.closest('.shopify-section, dialog');
        section?.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
        this.init();
    }

    disconnectedCallback() {
        const section = this.closest('.shopify-section, dialog');
        section?.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
        this.cleanup();
    }

    /** Rebind UI & listeners after render/morph */
    init() {
        this.cleanup();
        this.aborter = new AbortController();
        const { signal } = this.aborter;

        // --- helpers ---
        const toNumber = (val, fallback = 0) => {
            const n = Number(val);
            return Number.isFinite(n) ? n : fallback;
        };
        const formatMoney = (amount, code) => {
            try {
                return new Intl.NumberFormat(document.documentElement.lang || 'en', {
                    style: 'currency',
                    currency: code,
                }).format(amount);
            } catch {
                return amount.toFixed(2) + ' ' + code;
            }
        };
        const convertCents = (cents, rate) => (toNumber(cents) / 100) * toNumber(rate, 1);

        // --- currency context ---
        const shopifyCurrency = window.Shopify?.currency ?? {};
        let rate = toNumber(shopifyCurrency.rate, 1);
        const activeCode = shopifyCurrency.active || shopifyCurrency.currency || null;
        const fallbackCode = this.getAttribute('data-currency-code') || 'USD';
        let currencyCode = activeCode || fallbackCode;

        // Format displayed history prices
        this.querySelectorAll('.history-price').forEach((el) => {
            const cents = el.getAttribute('data-price-cents');
            const converted = convertCents(cents, rate);
            el.textContent = formatMoney(converted, currencyCode);
        });

        // --- popover ---
        const trigger = this.querySelector('[data-history-trigger]');
        const popover = this.querySelector('[data-history-popover]');
        const wrapper = trigger?.closest('.price-container') || this;

        const open = () => {
            if (!popover) return;
            popover.hidden = false;
            trigger?.setAttribute('aria-expanded', 'true');
        };
        const close = () => {
            if (!popover) return;
            popover.hidden = true;
            trigger?.setAttribute('aria-expanded', 'false');
        };

        if (trigger && popover && wrapper) {
            trigger.addEventListener(
                'click',
                (e) => {
                    e.stopPropagation();
                    popover.hidden ? open() : close();
                },
                { signal }
            );

            document.addEventListener(
                'click',
                (e) => {
                    if (!wrapper.contains(e.target)) close();
                },
                { signal }
            );

            document.addEventListener(
                'keydown',
                (e) => {
                    if (e.key === 'Escape') close();
                },
                { signal }
            );
        }

        // --- live currency changes ---
        ['currency:changed', 'shopify:currency-change'].forEach((evt) => {
            document.addEventListener(
                evt,
                () => {
                    rate = toNumber(window.Shopify?.currency?.rate, 1);
                    currencyCode = window.Shopify?.currency?.active || currencyCode;

                    this.querySelectorAll('.history-price').forEach((el) => {
                        const cents = el.getAttribute('data-price-cents');
                        const converted = convertCents(cents, rate);
                        el.textContent = formatMoney(converted, currencyCode);
                    });
                },
                { signal }
            );
        });
    }

    /** Remove listeners created in init() */
    cleanup() {
        this.aborter?.abort();
        this.aborter = null;
    }

    /** @param {CustomEvent} event */
    onVariantUpdate = (event) => {
        // Same product guard you used for inventory
        if (event.detail?.data?.newProduct) {
            this.dataset.productId = String(event.detail.data.newProduct.id);
        } else if (
            event.target instanceof HTMLElement &&
            event.target.dataset.productId !== this.dataset.productId
        ) {
            return;
        }

        const fresh = event.detail?.data?.html?.querySelector?.('product-price-history');
        if (!fresh) return;

        // Morph THIS element from the server-rendered HTML for the new variant
        morph(this, fresh, { childrenOnly: true });

        // Re-initialize after morph
        this.init();
    };
}

if (!customElements.get('product-price-history')) {
    customElements.define('product-price-history', ProductPriceHistory);
}
